import type { ConversationDTO, ConversationStatus } from '@eriko/shared-types';
import { cn } from '@eriko/ui/src/lib/utils';
import { useMemo, useState } from 'react';
import { ConvListItem } from './ConvListItem';

type FilterKey = 'all' | 'open' | 'needs_reply' | 'resolved';

const FILTERS: { key: FilterKey; label: string; match: (s: ConversationStatus) => boolean }[] = [
  { key: 'all', label: 'All', match: (s) => s !== 'ARCHIVED' },
  { key: 'open', label: 'Open', match: (s) => s === 'OPEN' },
  { key: 'needs_reply', label: 'Reply', match: (s) => s === 'NEEDS_REPLY' },
  { key: 'resolved', label: 'Done', match: (s) => s === 'RESOLVED' },
];

interface ConvListProps {
  conversations: ConversationDTO[];
  activeId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
}

export function ConvList({ conversations, activeId, onSelect, loading }: ConvListProps) {
  const [filter, setFilter] = useState<FilterKey>('all');

  const visible = useMemo(
    () => conversations.filter((c) => c.threadType !== 'REVIEWER_AI'),
    [conversations],
  );

  const counts = useMemo(
    () => ({
      all: visible.filter((c) => c.status !== 'ARCHIVED').length,
      open: visible.filter((c) => c.status === 'OPEN').length,
      needs_reply: visible.filter((c) => c.status === 'NEEDS_REPLY').length,
      resolved: visible.filter((c) => c.status === 'RESOLVED').length,
    }),
    [visible],
  );

  const rows = useMemo(() => {
    const active = FILTERS.find((f) => f.key === filter)!;
    const filtered = visible.filter((c) => active.match(c.status));
    filtered.sort((a, b) => {
      if (a.status === 'NEEDS_REPLY' && b.status !== 'NEEDS_REPLY') return -1;
      if (b.status === 'NEEDS_REPLY' && a.status !== 'NEEDS_REPLY') return 1;
      return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
    });
    return filtered;
  }, [visible, filter]);

  return (
    <div className="w-[280px] shrink-0 border-r border-surface-200 flex flex-col">
      {/* Filter tabs */}
      <nav className="px-2 py-1.5 flex border-b border-surface-200">
        {FILTERS.map((f) => {
          const isActive = filter === f.key;
          const count = counts[f.key];
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'flex-1 px-1 py-1 rounded-md text-[0.6875rem] font-medium transition-colors text-center',
                isActive
                  ? 'bg-surface-900 text-surface-0'
                  : 'text-surface-500 hover:bg-surface-100',
              )}
            >
              {f.label}
              {count > 0 && (
                <span className="ml-1 text-surface-400">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Conversation list */}
      <div className="flex-1 overflow-y-auto p-1.5">
        {loading && <p className="px-3 py-8 text-center text-xs text-surface-500">Loading…</p>}
        {!loading && rows.length === 0 && (
          <p className="px-3 py-8 text-center text-xs text-surface-500">No conversations.</p>
        )}
        {rows.map((conversation) => (
          <ConvListItem
            key={conversation.id}
            conversation={conversation}
            isActive={conversation.id === activeId}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
}
